import type { Theme } from '@mui/material';
import type { Components } from '@mui/material/styles';

import type {} from '@mui/x-date-pickers/themeAugmentation';

import { colors } from '../tokens';

export const MuiDateCalendar: Components<Theme>['MuiDateCalendar'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      width: '100%',
      height: 'auto',
      maxHeight: 'none',
      padding: theme.spacing(0.5, 0),
    }),
  },
};

export const MuiPickersDay: Components<Theme>['MuiPickersDay'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.spacing(1),
      fontSize: theme.typography.pxToRem(14),
      color: colors.black[950],

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },

      '&&.Mui-selected': {
        backgroundColor: colors.green[500],
        color: colors.common.white,

        '&:hover': {
          backgroundColor: colors.green[500],
        },
        '&:focus': {
          backgroundColor: colors.green[500],
        },
      },

      ...theme.applyStyles('dark', {
        color: colors.black[100],
      }),
    }),
  },
};

export const MuiPickersCalendarHeader: Components<Theme>['MuiPickersCalendarHeader'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    }),
    label: ({ theme }) => ({
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 600,
      color: colors.black[950],

      ...theme.applyStyles('dark', {
        color: colors.black[100],
      }),
    }),
  },
};

export const MuiDayCalendar: Components<Theme>['MuiDayCalendar'] = {
  styleOverrides: {
    weekDayLabel: ({ theme }) => ({
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 900,
      color: colors.black[600],

      ...theme.applyStyles('dark', {
        color: colors.black[300],
      }),
    }),
  },
};

export const MuiPickersOutlinedInput: Components<Theme>['MuiPickersOutlinedInput'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.spacing(1),

      '&:hover .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
      },

      '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },

      '&.Mui-disabled': {
        cursor: 'not-allowed',

        '& .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: theme.palette.divider,
        },
        '& svg': { color: colors.black[300] },
      },

      '&.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: colors.red[300],
      },
      '&.Mui-error.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: colors.red[500],
        borderWidth: '2px',
      },
    }),

    notchedOutline: ({ theme }) => ({
      top: 0,
      borderColor: theme.palette.divider,
      transition: 'border-color 0.2s, border-width 0.1s',

      '& legend': { display: 'none' },
    }),

    input: ({ theme }) => ({
      padding: 0,
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.spacing(3),
      color: colors.black[950],

      '&::placeholder': {
        color: colors.gray[300],
        opacity: 1,
        ...theme.applyStyles('dark', {
          color: colors.black[300],
        }),
      },

      '&:disabled': {
        color: colors.black[300],
        WebkitTextFillColor: colors.black[300],
        cursor: 'not-allowed',
        ...theme.applyStyles('dark', {
          color: colors.black[600],
          WebkitTextFillColor: colors.black[600],
        }),
      },

      ...theme.applyStyles('dark', {
        color: colors.black[25],
      }),
    }),
  },
};

export const MuiPickersPopper: Components<Theme>['MuiPickersPopper'] = {
  styleOverrides: {
    paper: ({ theme }) => ({
      borderRadius: theme.spacing(3),
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[4],
      marginTop: theme.spacing(0.5),
    }),
  },
};

// цей проп, потрібний по дефолту, бо reduceAnimations забирає анімацію при переключенні з календаря на вибір року
// https://mui.com/x/api/date-pickers/date-picker/
// https://github.com/mui/mui-x/issues/9895
export const MuiDatePicker: Components<Theme>['MuiDatePicker'] = {
  defaultProps: {
    reduceAnimations: true,
  },
};

// Default step is 5 minutes, which makes 23:55 the last selectable time of the day
export const MuiTimePicker: Components<Theme>['MuiTimePicker'] = {
  defaultProps: {
    timeSteps: { minutes: 1 },
  },
};
